import { ShieldCheck } from "@phosphor-icons/react";
import { type ComponentPropsWithoutRef, forwardRef } from "react";
import { useTranslation } from "react-i18next";

import { type AskPunttSource } from "./types";

export type InlineSourceCitationDensity = "cell" | "block";

export type InlineSourceCitationProps = Omit<
  ComponentPropsWithoutRef<"button">,
  "children"
> & {
  source: AskPunttSource;
  testIdPrefix: string;
  density?: InlineSourceCitationDensity;
  /** Extra sources beyond the primary title (renders as +N). */
  extraCount?: number;
  /** Visible text in place of the title, e.g. a citation number. Pair it with `ariaLabel`. */
  label?: string;
  /** Accessible name in place of the title's; must contain `label` (WCAG 2.5.3). */
  ariaLabel?: string;
};

/** The corner of a citation badge. The saved-memory chip sits beside these badges and takes the same one. */
export const SOURCE_BADGE_RADIUS_CLASS = "rounded-[3px]";

/**
 * The badge's look, shared by every surface that shows a citation number: the
 * in-text badge, the chips under an answer, and the rail's Cited claims cards
 * read as one numbered set. `selected` is the card or chip the reader focused.
 */
export function sourceBadgeClass(selected = false): string {
  return [
    `inline-flex items-center justify-center ${SOURCE_BADGE_RADIUS_CLASS} px-2 py-0.5 text-[12px] font-medium leading-4 tracking-[0.04px]`,
    selected
      ? "bg-puntt-accent-9 text-base-white"
      : "bg-puntt-neutral-gray-3 text-puntt-neutral-gray-11",
  ].join(" ");
}

// why forwardRef + rest props: SourceHoverCard renders this inside
// HoverCard.Trigger asChild, and the Slot hands its ref and pointer handlers to
// this component. Dropping them leaves the card unable to open on hover.
export const InlineSourceCitation = forwardRef<
  HTMLButtonElement,
  InlineSourceCitationProps
>(function InlineSourceCitation(
  {
    source,
    testIdPrefix,
    density = "block",
    extraCount = 0,
    label: labelOverride,
    ariaLabel: ariaLabelOverride,
    className,
    ...rest
  },
  ref,
) {
  const { t } = useTranslation("ui");
  const showShield = density === "block" && source.kind === "knowledge";
  const showExtra = extraCount > 0;
  const titleLabel = showExtra
    ? t("askPuntt.sources.inlineLabelWithExtra", {
        title: source.title,
        count: extraCount,
      })
    : source.title;
  const label = labelOverride ?? titleLabel;

  const ariaLabel =
    ariaLabelOverride ??
    (showExtra
      ? t("askPuntt.sources.inlineAriaWithExtra", {
          title: source.title,
          count: extraCount,
        })
      : t("askPuntt.sources.inlineAria", { title: source.title }));

  return (
    <button
      {...rest}
      ref={ref}
      type="button"
      data-testid={`${testIdPrefix}-inline-source-${source.id}`}
      aria-label={ariaLabel}
      className={[
        sourceBadgeClass(),
        "cursor-pointer gap-1 overflow-hidden text-left",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {showShield ? (
        <ShieldCheck
          size={12}
          aria-hidden="true"
          data-testid={`${testIdPrefix}-inline-source-shield-${source.id}`}
          className="shrink-0"
        />
      ) : null}
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
});
